import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IStore } from 'redux/IStore';
import { reduxForm, getFormSyncErrors, Field, FieldArray, change } from 'redux-form';
import { ErrorSummary, ItemCardList } from 'components';
import { ErrorPage } from 'containers';
import { PortalPageSection } from 'components/PortalStructure';
import { maxLength, required } from 'helpers/validationattributes';
import { InjectedRouter } from 'react-router';
import { showModal, closeModal } from 'modules/modal';
import { IModalAction } from 'models/modal';
import { IManageOutfit, IManageOutfitAction } from 'models/outfit/manageoutfit';
import { IOutfit } from 'models/outfit/outfit';
import { OutfitEditorHeader } from '../OutfitEditorHeader';
import { IItemCardList } from 'models/itemcard';
import { FormImages, PortalInputField, PortalSwitchField } from 'components/FormControls';
import { clearOutfitFormError, setOutfitValidationFailure } from 'redux/modules/outfit/manageoutfit';
import { INodeData } from 'models/common';
import { imgSrcAttributes } from 'helpers/common';
const { connect } = require('react-redux');
require('./style.css');

interface IProps {
	currentFormValues: IOutfit;
	initialValues: IManageOutfit;
	manageOutfit: IManageOutfit;
	location?: any;
	router: InjectedRouter;
	route: any;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	t?: (key: string) => string;
	onSubmit?: (form: IOutfit) => void;
	previousStep?: () => IManageOutfitAction;
	clearOutfitFormError: () => IManageOutfitAction;
	setOutfitValidationFailure: (message: any) => IManageOutfitAction;
	showModal: (modalType: string, modalProps: any) => IModalAction;
	closeModal: () => IModalAction;
	changeFormField: (form: string, field: string, data: any) => void;
}

const maxLength50 = maxLength(50);
const maxLength255 = maxLength(255);

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class StepOutfitForm extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.imageUploadFailed = this.imageUploadFailed.bind(this);
		this.imageUploadSuccess = this.imageUploadSuccess.bind(this);
		this.cancelClick = this.cancelClick.bind(this);
	}

	public onSubmit(form: IOutfit) {
		this.props.onSubmit(form);
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'name':
				return t('Outfit_CreateOutfit_StepOutfit_Label_Name');
			case 'description':
				return t('Outfit_CreateOutfit_StepOutfit_Label_Description');
			default:
				return key;
		}
	}

	private cancelClick() {
		this.props.closeModal();
	}

	private imageUploadSuccess() {
		this.props.clearOutfitFormError();
		this.forceUpdate();
	}

	private imageUploadFailed(error: any) {
		if (error) {
			this.props.setOutfitValidationFailure(error);
			this.forceUpdate();
		}
	}

	private getThumbnailImageFromItems(): JSX.Element {
		const images: JSX.Element[] = [];
		if (this.props.currentFormValues && this.props.currentFormValues.itemsJArray.length > 0) {
			this.props.currentFormValues.itemsJArray.map((x, i) => {
				const id = (JSON.parse(x.entityId) as INodeData).id;
				images.push(
					<div key={`outfit_item_${id}_${x.name}_${i}`} className="outfitItemImage">
						<img {...imgSrcAttributes(x.picture)} />
					</div>,
				);
			});
		}

		return <div className="outfitImage">{images}</div>;
	}

	private getOutfitImage(): JSX.Element {
		const { images } = this.props.currentFormValues;

		if (!images || images.length === 0) {
			return this.getThumbnailImageFromItems();
		} else {
			let imageHref = null;
			if (this.props.currentFormValues.selectedThumbnail && this.props.currentFormValues.selectedThumbnail.length > 0) {
				imageHref = this.props.currentFormValues.images.find(
					(x) => x.id === this.props.currentFormValues.selectedThumbnail,
				).href;
			} else if (
				this.props.currentFormValues &&
				this.props.currentFormValues.images &&
				this.props.currentFormValues.images.length > 0
			) {
				imageHref = this.props.currentFormValues.images[0].href;
			}
			return (
				<div className="outfitImage">
					<img {...imgSrcAttributes(imageHref)} />
				</div>
			);
		}
	}

	private getOutfitImages() {
		const images: JSX.Element[] = [];
		if (
			this.props.currentFormValues &&
			this.props.currentFormValues.images &&
			this.props.currentFormValues.images.length > 0
		) {
			this.props.currentFormValues.images.map((x, i) => {
				images.push(
					<div key={`outfit_images_${x.id}_${i}`} className="outfitAdditionalImage">
						<img {...imgSrcAttributes(x.href)} />
					</div>,
				);
			});
		}
		return <div className="outfitImages">{images}</div>;
	}

	public render() {
		// we also have error summary component, so if 422 let error pass to error summary
		if (this.props.manageOutfit.error && this.props.manageOutfit.message.status !== 422) {
			return <ErrorPage {...this.props} message={this.props.manageOutfit.message} />;
		}

		const {
			handleSubmit,
			formErrors,
			submitFailed,
			location,
			currentFormValues,
			pristine,
			submitting,
			t,
			changeFormField,
		} = this.props;
		const { error, message } = this.props.manageOutfit;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error,
			message,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const itemsJArray = currentFormValues.itemsJArray.map((x, i) => {
			return {
				...x,
				key: `${(JSON.parse(x.entityId) as INodeData).id}_${i}`,
			};
		});

		const outfitItemsListProps: IItemCardList = {
			items: itemsJArray,
			itemType: c.NODE_TYPE_OUTFIT_ITEM_SMALL_CARD,
		};

		const outfitEditorHeaderProps: any = {
			wizardStep: 2,
			title: t('Outfit_CreateOutfit_StepOutfit_Title'),
			previousPage: this.props.previousStep,
			cancelClick: this.cancelClick,
			pristine,
			submitting,
		};

		return (
			<div className="createOutfitOutfit">
				<div className="profileError">
					<ErrorSummary {...errorSummaryProps} />
				</div>
				<form onSubmit={handleSubmit(this.onSubmit)}>
					<OutfitEditorHeader {...outfitEditorHeaderProps} />
					<div className="container-fluid wrapperContentMiddle">
						<div className="row">
							<div className="col-md-9 leftBlock">
								<PortalPageSection>
									<div className="row">
										<div className="col-md-12">
											<Field
												name="name"
												type="text"
												component={PortalInputField}
												label={t('Outfit_CreateOutfit_StepOutfit_Placeholder_Name')}
												autoFocus={true}
												validate={[required, maxLength50]}
											/>
										</div>
									</div>
									<div className="row">
										<div className="col-md-12">
											<Field
												name="description"
												type="text"
												component={PortalInputField}
												label={t('Outfit_CreateOutfit_StepOutfit_Placeholder_Description')}
												validate={[maxLength255]}
											/>
										</div>
									</div>
									<div className="row">
										<div className="col-md-6">
											<Field
												name="published"
												component={PortalSwitchField}
												type="checkbox"
												label={t('Outfit_CreateOutfit_StepOutfit_Label_IsPublic')}
											/>
										</div>
									</div>
									<div className="row">
										<div className="col-md-3 leftBlockAdditionalImages">{this.getOutfitImages()}</div>
										<div className="col-md-6">{this.getOutfitImage()}</div>
										<div className="col-md-3" />
									</div>
								</PortalPageSection>
							</div>
							<div className="col-md-3 rightBlock">
								<h2>{t('Outfit_CreateOutfit_StepOutfit_Items_Title')}</h2>
								<div className="row">
									<ItemCardList {...outfitItemsListProps} />
								</div>
							</div>
						</div>
						<div className="row">
							<div className="col-md-12 uploadImageBottomSection">
								<div className="textStyleUploadImage">
									<FieldArray
										name="images"
										component={FormImages}
										images={currentFormValues ? currentFormValues.images : []}
										maxImageSize={c.MAX_PRODUCT_IMAGE_SIZE}
										imageUploadSuccess={this.imageUploadSuccess}
										imageUploadFailed={this.imageUploadFailed}
										selectedImages={currentFormValues.selectedImages}
										selectedThumbnail={currentFormValues.selectedThumbnail}
										selectedImagesFieldName={'selectedImages'}
										selectedThumbnailFieldName={'selectedThumbnail'}
										enableSelectThumbnail={true}
										enableSelectImage={true}
										label={t('Shop_ManageShop_Products_CreateProduct_Label_Images')}
										imageDescription={t('Shop_ManageShop_Products_CreateProduct_ImageUpload_Text_ImageDescription')}
										uploadDescription={t('Shop_ManageShop_Products_CreateProduct_ImageUpload_Text_UploadDescription')}
										change={changeFormField}
										cropImage={true}
										cropImageOptions={{
											...c.DEFAULT_CROP_OPTIONS,
											aspectRatio: 515 / 667,
										}}
										cropImageModalTitle={t('Shop_ManageShop_Products_CreateProduct_CropImageModal_Title')}
										showModal={this.props.showModal}
										closeModal={this.props.closeModal}
									/>
								</div>
							</div>
						</div>
					</div>
				</form>
			</div>
		);
	}
}

let StepOutfit = reduxForm({
	form: c.FORM_NAME_MANAGEOUTFIT,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(StepOutfitForm);

StepOutfit = connect(
	(state: IStore) => {
		let currentFormValues: IOutfit = null;
		if (state.form[c.FORM_NAME_MANAGEOUTFIT] && state.form[c.FORM_NAME_MANAGEOUTFIT].values) {
			currentFormValues = state.form[c.FORM_NAME_MANAGEOUTFIT].values;
		} else {
			currentFormValues = state.manageOutfit.outfit;
		}

		return {
			initialValues: state.manageOutfit.outfit,
			manageOutfit: state.manageOutfit,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_MANAGEOUTFIT)(state),
		};
	},
	(dispatch) => ({
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
		clearOutfitFormError: () => dispatch(clearOutfitFormError()),
		setOutfitValidationFailure: (message) => dispatch(setOutfitValidationFailure(message)),
		changeFormField: (form: string, field: string, data: any) => dispatch(change(form, field, data)),
	}),
)(StepOutfit);

export { StepOutfit };
